import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const VERTICAL_CATS = {
  law_enforcement: ["domestic_violence_call", "officer_involved_shooting", "child_welfare_call", "fatal_traffic_collision", "active_shooter_response"],
  fire_service: ["structure_fire_civilian_loss", "wildland_extended_deployment", "pediatric_cardiac_arrest", "line_of_duty_death", "hazmat_incident"],
  ems: ["pediatric_resuscitation", "mass_casualty_incident", "overdose_response", "patient_assault", "long_transport_terminal_patient"],
  dispatch_psap: ["active_caller_in_crisis", "officer_down_call", "lost_child_call", "suicide_in_progress", "mass_casualty_dispatch"],
  corrections: ["inmate_self_harm", "use_of_force", "contraband_search", "inmate_assault_on_staff", "mental_health_crisis_in_unit"],
  school_safety: ["lockdown_drill_real_threat", "student_in_crisis", "parent_confrontation", "weapon_on_campus", "post_incident_return"],
  military: ["combat_loss", "moral_injury_rules_of_engagement", "homecoming_reintegration", "leadership_attrition", "extended_deployment_isolation"],
  emergency_medicine: ["pediatric_death", "mass_trauma_event", "patient_aggression", "moral_distress_resource_allocation", "colleague_loss"],
};

export default function AgencyCriticalIncident() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [roster, setRoster] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [cat, setCat] = useState("");
  const [note, setNote] = useState("");
  const [all, setAll] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [preFilledFromEngagement, setPreFilledFromEngagement] = useState(false);

  useEffect(() => { api.get("/agency/roster").then(r => setRoster(r.data)).catch(() => {}); }, []);

  // Read URL pre-selection (?targets=u1,u2&source=engagement_new_hire)
  useEffect(() => {
    const targets = params.get("targets");
    if (targets) {
      const ids = targets.split(",").map(s => s.trim()).filter(Boolean);
      if (ids.length) {
        setSelected(new Set(ids));
        setAll(false);
        setPreFilledFromEngagement(true);
      }
    }
  }, [params]);

  const cats = VERTICAL_CATS[user?.vertical] || [];

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const push = async () => {
    if (!cat) { toast.error("Choose an incident category."); return; }
    setPushing(true);
    try {
      const body = {
        incident_category: cat,
        note,
        user_ids: all ? null : Array.from(selected),
      };
      const r = await api.post("/agency/push-critical-incident", body);
      toast.success(`Pushed to ${r.data.pushed} member${r.data.pushed === 1 ? "" : "s"}.`);
      setSelected(new Set()); setCat(""); setNote("");
    } catch {
      toast.error("Push failed.");
    } finally { setPushing(false); }
  };

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <h1 className="font-heading text-3xl font-bold">Critical Incident Push</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Trigger a single-scenario debrief for the entire agency or a targeted subset.
          Scenarios are constructed NOT to mirror the specific event — HMDE Call 1 checks for re-traumatization risk before delivery.
        </p>

        {preFilledFromEngagement && (
          <div data-testid="critical-prefilled-banner"
               className="mt-4 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs"
               style={{ borderColor: "hsl(var(--haruki-leaf))", background: "hsl(var(--haruki-leaf) / 0.08)", color: "hsl(var(--haruki-leaf-deep))" }}>
            <span className="font-semibold uppercase tracking-widest">Pre-filled from engagement</span>
            <span>· {selected.size} non-completer{selected.size === 1 ? "" : "s"} selected</span>
          </div>
        )}

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Configure push</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Incident category</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger data-testid="critical-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {cats.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (internal)</Label>
              <Textarea data-testid="critical-note-textarea" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="all" data-testid="critical-all-checkbox" checked={all} onCheckedChange={setAll} />
              <Label htmlFor="all" className="cursor-pointer">Push to entire agency</Label>
            </div>

            {!all && (
              <div className="space-y-2">
                <Label>Targeted members ({selected.size} selected)</Label>
                <div className="max-h-60 overflow-y-auto rounded-md border border-border">
                  {roster.map(m => (
                    <label key={m.user_id} className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-0">
                      <Checkbox
                        data-testid={`critical-target-${m.user_id}`}
                        checked={selected.has(m.user_id)}
                        onCheckedChange={() => toggle(m.user_id)}
                      />
                      <span className="text-sm">{m.name}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{m.vertical}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <Button
              data-testid="critical-push-button"
              onClick={push}
              disabled={pushing}
              className="w-full"
              style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
            >
              {pushing ? "Pushing…" : "Push critical incident debrief"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
